import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { ArrowRight, Check, Loader2, Mic, MicOff, Pencil, Sparkles } from 'lucide-react'
import { track } from '@adthlete/shared-frontend'
import { suggestedBonuses } from '@adthlete/common/utils/dealPricing'
import { useToast } from '@/components/ui/use-toast'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import { AGE_GROUPS, BUDGETS, GENDERS, US_STATES } from '@/lib/brandProfile'
import { DEAL_TYPE_OPTIONS } from '@/lib/dealTypes'
import {
  useCreateCampaignMutation,
  useGetSportsQuery,
  useInterpretIntakeMutation,
  useUpdateOrganizationMutation,
  type IntakeReading,
} from '@/redux/marketerApi'
import { fetchCurrentUser } from '@/redux/userSlice'
import type { AppDispatch } from '@/store'

type Step = 'brand' | 'goal' | 'customer' | 'budget' | 'reading'
const ORDER: Exclude<Step, 'reading'>[] = ['brand', 'goal', 'customer', 'budget']

// What brings brands here, in their words; each also marks a kind of deal
const GOAL_STARTERS: { label: string; deal: 'licensing' | 'gifting' | 'appearance' }[] = [
  { label: 'UGC for our ads', deal: 'licensing' },
  { label: 'Whitelisted or partnership ads', deal: 'licensing' },
  { label: 'Gifting with a guaranteed post', deal: 'gifting' },
  { label: 'Athletes at our store or event', deal: 'appearance' },
]

const QUESTIONS: Record<Exclude<Step, 'reading'>, { ask: string; sub: string }> = {
  brand: {
    ask: "Hi, I'm here to find you the right athletes. What's your brand called?",
    sub: 'The name athletes will see.',
  },
  goal: {
    ask: 'What brought you to Athios today?',
    sub: 'In your words. Tap any that fit, or type or say it.',
  },
  customer: {
    ask: "Who's your customer?",
    sub: 'Who you sell to, like “women 35 to 70 who run”. We match athletes whose audience looks like them.',
  },
  budget: {
    ask: 'What do you usually spend per piece of content?',
    sub: 'Pick any that fit. Skip it if you are not sure yet.',
  },
}

/** The browser's own speech-to-text, where it has one (Chrome, Edge, Safari) */
const SpeechRecognitionImpl: any =
  typeof window !== 'undefined'
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : null

/** A mic button that appends what's said to a text box */
const MicButton: React.FC<{ onText: (t: string) => void }> = ({ onText }) => {
  const [on, setOn] = useState(false)
  const rec = useRef<any>(null)
  useEffect(() => () => rec.current?.stop?.(), [])
  if (!SpeechRecognitionImpl) return null
  const toggle = () => {
    if (on) {
      rec.current?.stop()
      return
    }
    const r = new SpeechRecognitionImpl()
    r.lang = 'en-US'
    r.interimResults = false
    r.continuous = true
    r.onresult = (e: any) => {
      const said = Array.from(e.results as ArrayLike<any>)
        .slice(e.resultIndex)
        .map((x: any) => x[0]?.transcript ?? '')
        .join(' ')
        .trim()
      if (said) onText(said)
    }
    r.onend = () => setOn(false)
    r.onerror = () => setOn(false)
    rec.current = r
    r.start()
    setOn(true)
  }
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={on}
      aria-label={on ? 'Stop listening' : 'Say it instead'}
      title={on ? 'Stop listening' : 'Say it instead'}
      className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border',
        on
          ? 'animate-pulse border-[#DC2626] bg-[#FEF2F2] text-[#DC2626]'
          : 'border-border bg-card text-foreground hover:bg-muted',
      )}
    >
      {on ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
    </button>
  )
}

const Chip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({
  on,
  onClick,
  children,
}) => (
  <button
    type="button"
    aria-pressed={on}
    onClick={onClick}
    className={cn(
      'rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors',
      on
        ? 'border-[#9BD85A] bg-[#C0FD72] text-[#0F172A]'
        : 'border-border bg-card text-foreground hover:border-[#9BD85A]',
    )}
  >
    {children}
  </button>
)

const Bubble: React.FC<{ from: 'athios' | 'you'; children: React.ReactNode }> = ({
  from,
  children,
}) =>
  from === 'athios' ? (
    <div className="flex items-start gap-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#C0FD72]">
        <Sparkles className="h-4 w-4 text-[#0F172A]" />
      </span>
      <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-card px-4 py-3 text-foreground shadow-sm ring-1 ring-border">
        {children}
      </div>
    </div>
  ) : (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-tr-md bg-[#0F172A] px-4 py-3 text-[#FFFFFF]">
        {children}
      </div>
    </div>
  )

const toggle = <T,>(list: T[], v: T) =>
  list.includes(v) ? list.filter((x) => x !== v) : [...list, v]
const ageLabel = (id: string) => AGE_GROUPS.find((a) => a.id === id)?.label ?? id
const budgetLabel = (id: string) => BUDGETS.find((b) => b.id === id)?.label ?? id
const dealLabel = (id: string) => DEAL_TYPE_OPTIONS.find((d) => d.id === id)?.label ?? id

/**
 * A new brand's first screen: four quick questions (brand, what brought them, who their
 * customer is, what they spend), typed, tapped or spoken. The answers are read into the
 * brand's setup and a first draft campaign, and the brand lands in the Marketplace with
 * athletes ranked for it. The full setup form stays at /setup.
 */
const BrandIntake: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { toast } = useToast()
  const { marketer } = useAuth()
  const org = (marketer as any)?.org
  const { data: sports = [] } = useGetSportsQuery()
  const [interpret, { isLoading: reading }] = useInterpretIntakeMutation()
  const [saveOrg] = useUpdateOrganizationMutation()
  const [createCampaign] = useCreateCampaignMutation()

  const [step, setStep] = useState<Step>('brand')
  const [brand, setBrand] = useState<string>(org?.name ?? '')
  const [goal, setGoal] = useState('')
  const [deals, setDeals] = useState<string[]>([])
  const [customer, setCustomer] = useState('')
  const [ages, setAges] = useState<string[]>([])
  const [gender, setGender] = useState<'any' | 'female' | 'male'>('any')
  const [budgets, setBudgets] = useState<string[]>([])
  const [result, setResult] = useState<IntakeReading | null>(null)
  const [landing, setLanding] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    inputRef.current?.focus({ preventScroll: true })
  }, [step, result])

  const done = (s: Step) =>
    step === 'reading' || (ORDER as Step[]).indexOf(s) < (ORDER as Step[]).indexOf(step)
  const answerFor: Record<Exclude<Step, 'reading'>, string> = {
    brand: brand.trim(),
    goal: goal.trim() || deals.map(dealLabel).join(', '),
    customer:
      [
        customer.trim(),
        ages.length ? `ages ${ages.map(ageLabel).join(', ')}` : '',
        gender !== 'any' ? `mostly ${gender === 'female' ? 'women' : 'men'}` : '',
      ]
        .filter(Boolean)
        .join(' · ') || 'Not sure yet',
    budget: budgets.length ? budgets.map(budgetLabel).join(', ') : 'Not sure yet',
  }

  const next = async (from: Exclude<Step, 'reading'>) => {
    track('intake_answered', { step: from })
    const i = ORDER.indexOf(from)
    if (i < ORDER.length - 1) {
      setStep(ORDER[i + 1])
      return
    }
    setStep('reading')
    try {
      const r = await interpret({
        goal: goal.trim(),
        customer: customer.trim(),
        ages,
        gender,
        dealTypes: deals,
        budgets,
        brandName: brand.trim() || null,
      }).unwrap()
      setResult(r)
      track('intake_read', { mode: r.mode })
    } catch (err: any) {
      toast({
        title: "Couldn't read that just now",
        description: err?.data?.error ?? 'Try again in a moment.',
        variant: 'destructive',
      })
      setStep('budget')
    }
  }

  const land = async () => {
    if (!result) return
    setLanding(true)
    try {
      await saveOrg({
        looking_for: {
          deal_types: result.dealTypes,
          sports: result.sports,
          other_sports: [],
          states: result.states,
          gender: result.gender,
          budgets: result.budgets,
          about: goal.trim() || null,
          customer: result.customer,
          ages: result.ages,
        },
        ...(result.dealTypes.includes('gifting') ? { gifting_bonuses: suggestedBonuses() } : {}),
      }).unwrap()
      // A first draft campaign from the answers, so the match (and "Work with…") works at once.
      // Drafts aren't shown to athletes; the brand opens it to athletes when it's ready.
      const res: any = await createCampaign({
        basics: {
          brand: brand.trim() || org?.name || 'My brand',
          logoUrl: null,
          name: result.campaignName,
          dateType: null,
          startDate: null,
          endDate: null,
          socialPlatform: ['Instagram', 'TikTok'],
          budget: null,
          licensing: result.dealType === 'licensing',
          dealType: result.dealType,
          openToApplications: false,
          applicationDeadline: null,
          payout: null,
          giftingBonuses: null,
          audience: { gender: result.gender, states: result.states, sports: result.sports },
        },
        targeting: {},
        creative: {
          analyzedBrief: {
            keyThemes: result.keywords,
            targetAudience: result.customer ?? '',
            tags: [],
            tone: '',
            objectives: [],
          },
        },
        selectedAthletes: [],
        invitedAthletes: [],
        saveAsDraft: true,
      } as any).unwrap()
      await dispatch(fetchCurrentUser())
      try {
        sessionStorage.setItem('athios_intake_summary', result.summary)
      } catch {
        // The banner just won't show the summary
      }
      track('intake_landed')
      navigate(`/marketplace?campaignId=${res.data.id}&welcome=1`, { replace: true })
    } catch (err: any) {
      toast({
        title: "Couldn't set that up",
        description: err?.data?.error ?? 'Try again in a moment.',
        variant: 'destructive',
      })
      setLanding(false)
    }
  }

  const fieldCls =
    'min-h-11 w-full rounded-xl border border-border bg-card px-4 py-2.5 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40'
  const appendSaid = (set: (f: (v: string) => string) => void) => (said: string) =>
    set((v) => (v.trim() ? `${v.trim()} ${said}` : said))

  const question = (s: Exclude<Step, 'reading'>) => (
    <Bubble from="athios">
      <p className="font-semibold">{QUESTIONS[s].ask}</p>
      {step === s ? (
        <p className="mt-0.5 text-sm text-muted-foreground">{QUESTIONS[s].sub}</p>
      ) : null}
    </Bubble>
  )

  const answerBox = (s: Exclude<Step, 'reading'>) => {
    const canGo = s === 'brand' ? brand.trim().length >= 2 : s === 'goal' ? !!answerFor.goal : true
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (canGo) next(s)
        }}
        className="space-y-3 rounded-2xl border border-[#D9F99D] bg-[#F7FEE7] p-4"
      >
        {s === 'brand' ? (
          <input
            ref={(el) => {
              inputRef.current = el
            }}
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            maxLength={120}
            placeholder="Your brand"
            aria-label="Brand name"
            className={fieldCls}
          />
        ) : null}

        {s === 'goal' ? (
          <>
            <div className="flex flex-wrap gap-2">
              {GOAL_STARTERS.map((g) => (
                <Chip
                  key={g.label}
                  on={goal.toLowerCase().includes(g.label.toLowerCase())}
                  onClick={() => {
                    setDeals((d) => (d.includes(g.deal) ? d : [...d, g.deal]))
                    setGoal((v) =>
                      v.toLowerCase().includes(g.label.toLowerCase())
                        ? v
                        : v.trim()
                          ? `${v.trim()}; ${g.label}`
                          : g.label,
                    )
                  }}
                >
                  {g.label}
                </Chip>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <textarea
                ref={(el) => {
                  inputRef.current = el
                }}
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    if (canGo) next(s)
                  }
                }}
                rows={2}
                maxLength={1000}
                placeholder="We need a steady flow of UGC for Meta, and want to try athletes"
                aria-label="What brought you here"
                className={cn(fieldCls, 'resize-none')}
              />
              <MicButton onText={appendSaid(setGoal)} />
            </div>
          </>
        ) : null}

        {s === 'customer' ? (
          <>
            <div className="flex items-end gap-2">
              <textarea
                ref={(el) => {
                  inputRef.current = el
                }}
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    next(s)
                  }
                }}
                rows={2}
                maxLength={500}
                placeholder="Women 35 to 70 who run and care about recovery"
                aria-label="Who your customer is"
                className={cn(fieldCls, 'resize-none')}
              />
              <MicButton onText={appendSaid(setCustomer)} />
            </div>
            <div>
              <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Ages
              </p>
              <div className="flex flex-wrap gap-2">
                {AGE_GROUPS.map((a) => (
                  <Chip
                    key={a.id}
                    on={ages.includes(a.id)}
                    onClick={() => setAges((x) => toggle(x, a.id))}
                  >
                    {a.label}
                  </Chip>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Mostly
              </p>
              <div className="flex flex-wrap gap-2">
                {GENDERS.map((g) => (
                  <Chip
                    key={g.id}
                    on={gender === g.id}
                    onClick={() => setGender(g.id as 'any' | 'female' | 'male')}
                  >
                    {g.label}
                  </Chip>
                ))}
              </div>
            </div>
          </>
        ) : null}

        {s === 'budget' ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {BUDGETS.map((b) => {
              const on = budgets.includes(b.id)
              return (
                <button
                  key={b.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setBudgets((x) => toggle(x, b.id))}
                  className={cn(
                    'flex items-center justify-between rounded-xl border px-4 py-3 text-left',
                    on
                      ? 'border-[#9BD85A] bg-[#FFFFFF]'
                      : 'border-border bg-card hover:border-[#9BD85A]',
                  )}
                >
                  <span>
                    <span className="block text-sm font-semibold text-foreground">{b.label}</span>
                    <span className="block text-xs text-muted-foreground">{b.blurb}</span>
                  </span>
                  {on ? <Check className="h-4 w-4 text-[#3F7A0A]" strokeWidth={3} /> : null}
                </button>
              )
            })}
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-3">
          {s !== 'brand' && s !== 'goal' ? (
            <button
              type="button"
              onClick={() => next(s)}
              className="text-sm font-semibold text-muted-foreground underline"
            >
              Skip
            </button>
          ) : (
            <span />
          )}
          <button
            type="submit"
            disabled={!canGo}
            className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-[#0F172A] px-5 text-sm font-semibold text-[#FFFFFF] disabled:opacity-40"
          >
            {s === 'budget' ? 'Find my athletes' : 'Next'} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </form>
    )
  }

  const sportName = (id: number) => sports.find((x) => x.id === id)?.name ?? ''
  const stateName = (code: string) => US_STATES.find((x) => x.code === code)?.name ?? code

  return (
    <div className="min-h-dvh bg-[#F6F7F2]">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 pb-16 pt-8 sm:pt-12">
        <div className="mb-2">
          <p className="text-xs font-bold uppercase tracking-wider text-[#4D7C0F]">
            Welcome to Athios
          </p>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-foreground">
            Let's find your athletes
          </h1>
          <p className="mt-1 text-muted-foreground">
            Four quick questions, then you're in the Marketplace with athletes ranked for you.
          </p>
        </div>

        {ORDER.map((s) =>
          done(s) || step === s ? (
            <React.Fragment key={s}>
              {question(s)}
              {done(s) ? (
                <Bubble from="you">
                  <div className="flex items-start gap-2">
                    <span className="min-w-0 flex-1">{answerFor[s]}</span>
                    {step !== 'reading' || result ? (
                      <button
                        type="button"
                        onClick={() => {
                          setResult(null)
                          setStep(s)
                        }}
                        aria-label="Change this answer"
                        className="shrink-0 text-[#CBD5E1] hover:text-[#FFFFFF]"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                </Bubble>
              ) : (
                answerBox(s)
              )}
            </React.Fragment>
          ) : null,
        )}

        {step === 'reading' ? (
          reading || !result ? (
            <Bubble from="athios">
              <p className="inline-flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Reading that and lining up athletes…
              </p>
            </Bubble>
          ) : (
            <>
              <Bubble from="athios">
                <p>{result.summary}</p>
              </Bubble>
              <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  What we'll look for
                </p>
                <dl className="mt-3 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-muted-foreground">You want</dt>
                    <dd className="font-semibold text-foreground">
                      {result.dealTypes.map(dealLabel).join(', ')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Your customer</dt>
                    <dd className="font-semibold text-foreground">
                      {[
                        result.customer,
                        result.ages.length ? `ages ${result.ages.map(ageLabel).join(', ')}` : '',
                        result.gender !== 'any'
                          ? `mostly ${result.gender === 'female' ? 'women' : 'men'}`
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'Anyone'}
                    </dd>
                  </div>
                  {result.keywords.length ? (
                    <div>
                      <dt className="text-muted-foreground">Content about</dt>
                      <dd className="font-semibold text-foreground">
                        {result.keywords.join(', ')}
                      </dd>
                    </div>
                  ) : null}
                  {result.states.length || result.sports.length ? (
                    <div>
                      <dt className="text-muted-foreground">Only</dt>
                      <dd className="font-semibold text-foreground">
                        {[...result.sports.map(sportName), ...result.states.map(stateName)]
                          .filter(Boolean)
                          .join(', ')}
                      </dd>
                    </div>
                  ) : null}
                  <div>
                    <dt className="text-muted-foreground">Your first campaign</dt>
                    <dd className="font-semibold text-foreground">
                      {result.campaignName}{' '}
                      <span className="font-normal text-muted-foreground">
                        (a draft only you see)
                      </span>
                    </dd>
                  </div>
                </dl>
                <button
                  type="button"
                  onClick={land}
                  disabled={landing}
                  className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#C0FD72] text-base font-semibold text-[#0F172A] hover:bg-[#B2F25E] disabled:opacity-60"
                >
                  {landing ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  Show me athletes <ArrowRight className="h-5 w-5" />
                </button>
                <p className="mt-2 text-center text-xs text-muted-foreground">
                  Change an answer with the pencil, or{' '}
                  <Link to="/setup" className="font-semibold text-[#3F7A0A] underline">
                    fill in the full brand form
                  </Link>{' '}
                  instead.
                </p>
              </section>
            </>
          )
        ) : null}

        {step !== 'reading' ? (
          <p className="text-center text-xs text-muted-foreground">
            Prefer a form?{' '}
            <Link to="/setup" className="font-semibold text-[#3F7A0A] underline">
              Fill in the full brand profile
            </Link>
          </p>
        ) : null}
        <div ref={endRef} />
      </div>
    </div>
  )
}

export default BrandIntake
